/**
 * Módulo de equipos — CRUD, estadísticas, documentos con versiones, reportes de vencimiento y descargas
 */

import { createCrudMethods } from '../base/crudMethods';
import { downloadBlobFromEndpoint } from '../../utils/downloadHelpers';

/**
 * Crea el módulo de equipos
 *
 * @param {Object} deps - Dependencias inyectadas
 * @param {Function} deps.authenticatedRequest - Función de request autenticada
 * @param {Function} deps.authenticatedFormDataRequest - Función para uploads y descargas
 * @returns {Object} Métodos del módulo equipos
 */
export function createEquiposModule({ authenticatedRequest, authenticatedFormDataRequest }) {
  return {
    // CRUD de equipos
    ...createCrudMethods(
      {
        endpoint: '/api/equipos',
        entityName: 'equipo'
      },
      { authenticatedRequest }
    ),

    // CRUD de documentos equipo (solo GET list y DELETE; upload/update usan FormData)
    ...createCrudMethods(
      {
        endpoint: '/api/documentos-equipo',
        entityName: 'documentoEquipo',
        entityNamePlural: 'documentosEquipo',
        methods: { getById: false, create: false, update: false }
      },
      { authenticatedRequest }
    ),

    /**
     * Obtiene estadísticas de equipos para dashboard
     *
     * @returns {Promise<{success: boolean, data: Object}>}
     */
    async getEquipoStats() {
      return await authenticatedRequest('/api/equipos/stats/resumen');
    },

    /**
     * Sube un nuevo documento de equipo
     *
     * @param {FormData} formData - Debe contener: archivo, equipo_id, tipo_documento_id
     * @returns {Promise<{success: boolean, data: Object, message?: string}>}
     */
    async uploadDocumentoEquipo(formData) {
      return authenticatedFormDataRequest('/api/documentos-equipo/upload', {
        method: 'POST',
        body: formData
      });
    },

    /**
     * Actualiza un documento de equipo (puede incluir nuevo archivo)
     *
     * @param {number} id - ID del documento a actualizar
     * @param {FormData} formData - FormData con los datos actualizados
     * @returns {Promise<{success: boolean, message?: string}>}
     */
    async updateDocumentoEquipo(id, formData) {
      return authenticatedFormDataRequest(`/api/documentos-equipo/${id}`, {
        method: 'PUT',
        body: formData
      });
    },

    /**
     * Descarga un documento de equipo
     *
     * @param {number} id - ID del documento a descargar
     * @returns {Promise<{blob: Blob, filename: string}>}
     */
    async downloadDocumentoEquipo(id) {
      return downloadBlobFromEndpoint(authenticatedFormDataRequest, `/api/documentos-equipo/${id}/download`, 'documento');
    },

    /**
     * Exporta todos los documentos de un equipo como ZIP
     *
     * @param {number} equipoId - ID del equipo cuyos documentos exportar
     * @returns {Promise<{blob: Blob, filename: string}>}
     */
    async exportAllDocumentosEquipo(equipoId) {
      return downloadBlobFromEndpoint(authenticatedFormDataRequest, `/api/documentos-equipo/equipo/${equipoId}/export-all`, 'documentos.zip');
    },

    /**
     * Obtiene documentos próximos a vencer
     *
     * @param {number} [dias=30] - Días a futuro para buscar
     * @returns {Promise<{success: boolean, data: Array}>}
     */
    async getDocumentosVencimientosProximos(dias = 30) {
      return await authenticatedRequest(`/api/documentos-equipo/vencimientos/proximos?dias=${dias}`);
    },

    /**
     * Obtiene estadísticas de vencimientos para dashboard
     *
     * @returns {Promise<{success: boolean, data: Object}>}
     */
    async getDocumentosVencimientosStats() {
      return await authenticatedRequest('/api/documentos-equipo/vencimientos/stats');
    },

    /**
     * Obtiene historial de versiones de un documento
     *
     * @param {number} documentoId - ID del documento
     * @returns {Promise<{success: boolean, data: Array}>}
     */
    async getVersionesDocumento(documentoId) {
      return await authenticatedRequest(`/api/documentos-equipo/${documentoId}/versiones`);
    },

    /**
     * Descarga una versión específica de un documento
     *
     * @param {number} documentoId - ID del documento
     * @param {number} versionNum - Número de versión a descargar
     * @returns {Promise<{blob: Blob, filename: string}>}
     */
    async downloadVersionDocumento(documentoId, versionNum) {
      return downloadBlobFromEndpoint(authenticatedFormDataRequest, `/api/documentos-equipo/${documentoId}/versiones/${versionNum}/download`, 'documento');
    },

    /**
     * Crea un reporte de problema en documento
     *
     * @param {Object} data - { documento_equipo_id, tipo_problema, descripcion }
     * @returns {Promise<{success: boolean, message: string, data: {id: number}}>}
     */
    async createDocumentoReporte(data) {
      return await authenticatedRequest('/api/reportes-documento', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Obtiene reportes pendientes para dashboard
     *
     * @param {Object} [params] - { limit, page }
     * @returns {Promise<{success: boolean, data: Array, pagination: Object}>}
     */
    async getReportesPendientes(params = {}) {
      const queryString = new URLSearchParams(params).toString();
      const endpoint = queryString
        ? `/api/reportes-documento/pendientes?${queryString}`
        : '/api/reportes-documento/pendientes';
      return await authenticatedRequest(endpoint);
    },

    /**
     * Obtiene estadísticas de reportes
     *
     * @returns {Promise<{success: boolean, data: {pendientes: number, resueltos: number, descartados: number}}>}
     */
    async getReportesStats() {
      return await authenticatedRequest('/api/reportes-documento/stats');
    },

    /**
     * Obtiene reportes de un documento específico
     *
     * @param {number} documentoId - ID del documento a consultar
     * @returns {Promise<{success: boolean, data: Array}>}
     */
    async getReportesByDocumento(documentoId) {
      return await authenticatedRequest(`/api/reportes-documento/documento/${documentoId}`);
    },

    /**
     * Cierra un reporte (resolver o descartar)
     *
     * @param {number} id - ID del reporte
     * @param {string} accion - 'resuelto' o 'descartado'
     * @param {File} evidencia - Archivo de imagen como evidencia (obligatorio)
     * @param {string} [notas] - Notas de cierre
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async cerrarReporte(id, accion, evidencia, notas = '') {
      const formData = new FormData();
      formData.append('accion', accion);
      formData.append('evidencia', evidencia);
      if (notas) formData.append('notas_resolucion', notas);

      return await authenticatedFormDataRequest(`/api/reportes-documento/${id}/cerrar`, {
        method: 'PUT',
        body: formData
      });
    }
  };
}
